import { IPhotoAlbum, IPhotoAlbumRaw } from '@ftd/types'

import { BASE_URL } from '@/api/constants'

import { secureFetch } from '../secure-fetch'

const PHOTO_ALBUMS_URL = `${BASE_URL}/photo-albums`

export const apiGetPhotoAlbums = async (
  sessionId?: string,
  catchId?: string,
  noteId?: string,
): Promise<IPhotoAlbumRaw[]> => {
  let url: string

  if (sessionId) {
    url = `${PHOTO_ALBUMS_URL}/session/${sessionId}`
  } else if (catchId) {
    url = `${PHOTO_ALBUMS_URL}/catch/${catchId}`
  } else {
    const urlObj = new URL(PHOTO_ALBUMS_URL)
    if (noteId) urlObj.searchParams.set('noteId', noteId)
    url = urlObj.toString()
  }

  const res = await secureFetch(url)
  if (!res.ok) throw new Error('Failed to fetch photo albums')
  return res.json()
}

export const apiGetPhotoAlbum = async (id: string): Promise<IPhotoAlbumRaw> => {
  const res = await secureFetch(`${PHOTO_ALBUMS_URL}/${id}`)
  if (!res.ok) throw new Error('Failed to fetch photo album')
  return res.json()
}

type CreatePhotoAlbumParams = {
  name: string
  description?: string
  sessionId?: string
  catchId?: string
}

export const apiCreatePhotoAlbum = async (data: CreatePhotoAlbumParams): Promise<IPhotoAlbum<string>> => {
  const { sessionId, catchId, ...rest } = data
  const res = await secureFetch(PHOTO_ALBUMS_URL, {
    method: 'POST',
    body: JSON.stringify({
      ...rest,
      sessionId,
      catchId,
    }),
  })
  if (!res.ok) throw new Error('Failed to create photo album')
  return res.json()
}

type UpdatePhotoAlbumData = {
  name?: string
  description?: string
  photos?: string[]
  session?: string
  catch?: string
  location?: string
  isDefault?: boolean
}

export const apiUpdatePhotoAlbum = async (id: string, data: UpdatePhotoAlbumData): Promise<IPhotoAlbumRaw> => {
  const res = await secureFetch(`${PHOTO_ALBUMS_URL}/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
  if (!res.ok) throw new Error('Failed to update photo album')
  return res.json()
}

export const apiDeletePhotoAlbum = async (id: string): Promise<void> => {
  const res = await secureFetch(`${PHOTO_ALBUMS_URL}/${id}`, {
    method: 'DELETE',
  })
  if (!res.ok) throw new Error('Failed to delete photo album')
}

export const apiSetCoverPhoto = async (albumId: string, photoId: string): Promise<IPhotoAlbum<string>> => {
  const res = await secureFetch(`${PHOTO_ALBUMS_URL}/${albumId}/cover-photo`, {
    method: 'PUT',
    body: JSON.stringify({ photoId }),
  })
  if (!res.ok) throw new Error('Failed to set cover photo')
  return res.json()
}

export const apiUpdatePhotoSharing = async (
  albumId: string,
  publicPhotoIds: Array<string>,
): Promise<IPhotoAlbumRaw> => {
  const res = await secureFetch(`${PHOTO_ALBUMS_URL}/public/${albumId}/update-photo-sharing`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ publicPhotoIds }),
  })
  if (!res.ok) throw new Error('Failed to update photo sharing')
  return res.json()
}

export const apiGetAllUserPhotoAlbums = async (shareType?: 'public' | 'private'): Promise<IPhotoAlbumRaw[]> => {
  const url = new URL(`${PHOTO_ALBUMS_URL}`)
  if (shareType) url.searchParams.set('shareType', shareType)
  const res = await secureFetch(url.toString(), {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  })
  if (!res.ok) throw new Error('Failed to get all user photo albums')
  return res.json()
}
